import { Injectable } from '@angular/core';
import { LocationService } from './location.service';
import { Guid } from '../core/common';
import { JustEat } from '../model/import.model';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';


@Injectable({
  providedIn: 'root'
})
export class ImportService {
  constructor(private locationService: LocationService, private http: HttpClient) { }

  async getProcessed(criteria: any) {
    return await firstValueFrom(this.http.post<{ list: any[], total: number }>('/api/britext/processed', criteria));
  }
  async getProcessedCsv(criteria: any) {
    const { csv } = await firstValueFrom(this.http.post<any>('/api/britext/processed/csv', criteria));
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${criteria.Query || ''}business.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  async grabArea(url: string) {
    return await firstValueFrom(this.http.post<{ outcode: string, message: string }>('/api/britext/grab-area', { Url: url }));
  }
  async grabRestaurant(url: string) {
    return await firstValueFrom(this.http.post<{ id: string, key: string, message: string }>('/api/britext/grab-restaurant', { Url: url }));
  }
  async getScrape(id: string, force = false) {
    return await firstValueFrom(this.http.get<any>('/api/britext/scrape/' + id, force ? { params: { force } } : undefined));
  }

  data: JustEat;
  async import(data: string) {
    const parsed = JSON.parse(data);
    // this.data = parsed.props.appProps.preloadedState.menu.restaurant.cdn;
    this.data = parsed;
    await this.makeBusiness();
    this.makeMenuOptions();
    this.makeMenus();
    this.makeMenuItems();
    this.makePromotions();
    return {
      business: this.business,
      menuOptions: this.menuOptions,
      menus: this.menus,
      menuItems: this.menuItems,
      promotions: this.promotions
    };
  }

  private business: any;
  private async makeBusiness() {
    var location = await this.locationService.getPostcode(this.data.restaurant.restaurantInfo.location.postCode);
    var cuisines = this.data.restaurant.restaurantInfo.cuisineTypes;
    this.business = {
      Id: Guid.newGuid(),
      Name: this.data.restaurant.restaurantInfo.seoName,
      Domains: [`${this.data.restaurant.restaurantInfo.seoName}.${window.location.hostname.replace('dashboard.', '')}`],
      Location: {
        Addresses: this.data.restaurant.restaurantInfo.location.address?.split('\n') || [],
        Postcode: this.data.restaurant.restaurantInfo.location.postCode,
        City: this.data.restaurant.restaurantInfo.location.city || location.City,
        Country: location.Country,
        Latitude: this.data.restaurant.restaurantInfo.location.latitude || location.Latitude,
        Longitude: this.data.restaurant.restaurantInfo.location.longitude || location.Longitude,
        Phone: this.data.restaurant.restaurantInfo.allergenPhoneNumber
      },
      OrderTypes: this.data.restaurant.restaurantInfo.restaurantOpeningTimes.map((x: any) => this.inferOrderType(x.serviceType)),
      OperatingHours: this.mapToOperatingHours(this.data.restaurant.restaurantInfo.restaurantOpeningTimes),
      DeliveryZones: [],
      Staffs: [],
      CuisineTypes: cuisines.map((x: any) => x.name),
      IsMock: true,
      UI: {
        Title: this.data.restaurant.restaurantInfo.name,
        Logo: this.data.restaurant.restaurantInfo.logoUrl,
        Description: this.data.restaurant.restaurantInfo.description,
        // BackgroundImage: `https://just-eat-prod-eu-res.cloudinary.com/image/upload/c_fill,f_auto,q_auto,w_1600,h_500,d_uk:cuisines:${cuisines[0].seoName || 'pizza'}-1.jpg/v1/uk/restaurants/${this.data.state.restaurantId}.jpg`,
        AllergenContact: this.data.restaurant.restaurantInfo.allergenPhoneNumber,
      }
    };

    if (this.business.Location.Postcode) {
      var location = await this.locationService.getPostcode(this.business.Location.Postcode);
      if (location)
        this.business.Location = Object.assign(this.business.Location, location);
    }
  }

  private menuOptions: any[];
  private makeMenuOptions() {
    const menuOptions: Map<string, any> = new Map();

    this.data.items.filter(_ => _.type == 'menuitem').forEach((item: any) => {
      item.variations.filter(_ => _.name).forEach((variation: any) => {
        if (!menuOptions.has(variation.name))
          menuOptions.set(variation.name, {
            Id: Guid.newGuid(),
            BusinessId: this.business.Id,
            Type: this.inferOptionTypeFromName(variation.name),
            Name: variation.name,
            Order: menuOptions.size
          });
      });
    });

    for (var modifierSet of this.data.modifierSets) {
      let groups = this.getModifierGroupPrices(modifierSet.id);
      if (!menuOptions.has(modifierSet.modifier.name))
        menuOptions.set(modifierSet.modifier.name, {
          Id: Guid.newGuid(),
          BusinessId: this.business.Id,
          Type: this.inferOptionTypeFromName(modifierSet.modifier.name),
          Name: modifierSet.modifier.name,
          Price: modifierSet.modifier.additionPrice,
          Order: menuOptions.size,
          _ModifierGroupPrices: groups
        });
      else {
        let option = menuOptions.get(modifierSet.modifier.name);
        option._ModifierGroupPrices = { ...option._ModifierGroupPrices || {}, ...groups };
      }
    }

    this.menuOptions = Array.from(menuOptions.values());

    for (var option of this.menuOptions) {
      if (option._ModifierGroupPrices != null) {
        let prices = Object.values(option._ModifierGroupPrices);
        if (prices.every((val, i, arr) => val === arr[0])) {
          option.Price = prices[0];
          // option._ModifierGroupPrices;
          continue;
        }
      }
      delete option.Price;
    }
  }

  private menus: any[];
  private makeMenus() {
    const menus: Map<string, any> = new Map();

    var itemIds = this.data.items.filter(_ => _.type == 'menuitem').map(_ => _.id);

    this.data.restaurant.menus.map((menu: any) => {
      menu.categories.filter(c => c.itemIds.some(i => itemIds.includes(i))).map((cat: any) => {
        if (!menus.has(cat.id))
          menus.set(cat.id, {
            Id: Guid.newGuid(),
            BusinessId: this.business.Id,
            Name: cat.name,
            CourseType: this.inferMenuTypeFromName(cat.name),
            UI: {
              Description: cat.description
            },
            Order: menus.size,
            _JustEatItemIds: [],
            _JustEatServiceTypes: []
          });
        let _menu = menus.get(cat.id);
        _menu._JustEatItemIds.push(...cat.itemIds);
        _menu._JustEatServiceTypes.push(...menu.serviceTypes);
      });
    });

    this.menus = Array.from(menus.values());
  }

  private menuItems: any[];
  private makeMenuItems() {
    const menuItems: Map<string, any> = new Map();

    this.data.items.filter(_ => _.type == 'menuitem').forEach((item: any, index: number) => {
      if (!menuItems.has(item.id))
        menuItems.set(item.id, {
          Id: Guid.newGuid(),
          BusinessId: this.business.Id,
          Name: item.name,
          BasePrice: [],
          ServingHours: [],
          Options: [],
          MenuIds: [],
          UI: {
            Description: item.description,
            // Image: item.imageSources?.map(_ => decodeURI(_.path.replace('{transformations}', ''))).join(','),
          },
          Order: menuItems.size
        });

      let menuItem = menuItems.get(item.id);

      var menus = this.menus.filter(_ => _._JustEatItemIds.includes(item.id));

      var basePrice = item.variations.find(_ => _.type == 'NoVariation')?.basePrice;
      if (basePrice) {
        // menus.flatMap(_ => _._JustEatServiceTypes).filter((value, index, self) => self.indexOf(value) === index)
        //   .forEach(orderType => {
        menuItem.BasePrice.push({ /*OrderType: orderType,*/ Price: basePrice });
        // });
      }

      menuItem.MenuIds.push(...menus.map(_ => _.Id));
      if (menus[0]?._JustEatItemIds.length)
        menuItem.Order = menus[0]._JustEatItemIds.indexOf(item.id);
      else menuItem.Order += menuItems.size;

      var variations = item.variations.filter(_ => _.type == 'Variation');
      if (variations.length) {
        var type = this.menuOptions.find(_ => variations.some(variation => _.Name == variation.name))?.Type;
        menuItem.Options.push({
          Name: 'Choose your variation',
          IsVariation: true,
          Type: type || 'Custom',
          Choices: variations.map((variation: any) => {
            var option = this.menuOptions.find(_ => _.Name == variation.name);
            return {
              MenuOptionId: option.Id,
              Prices: [{ Price: variation.basePrice }],
              _ModifierGroupIds: variation.modifierGroupsIds
            };
          }),
          MinSelection: 1,
          MaxSelection: 1,
        });
      }

      // item.variations.forEach((variation: any) => {
      menuItem.Options.push(...item.variations[0].modifierGroupsIds.map((modifierGroupId: string) => {
        let options = this.menuOptions.filter(o => o._ModifierGroupPrices?.[modifierGroupId] != undefined);
        let modifier = this.data.modifierGroups.find(_ => _.id == modifierGroupId);
        return {
          Name: modifier.name,
          Type: options[0]?.Type || 'Custom',
          Choices: options.map((option: any) => {
            var prices = [];
            if (option.Price == undefined && Object.keys(option._ModifierGroupPrices).length)
              for (var key in option._ModifierGroupPrices) {
                var chosen = menuItem.Options.find(_ => _.Choices?.some(_ => _._ModifierGroupIds?.includes(key)))?.Choices?.find(_ => _._ModifierGroupIds?.includes(key));
                if (chosen)
                  prices.push({
                    MenuOptionId: chosen.MenuOptionId,
                    Price: option._ModifierGroupPrices[key]
                  });
                else if (option._ModifierGroupPrices[key] != null)
                  prices.push({ Price: option._ModifierGroupPrices[key] });
              }
            return {
              MenuOptionId: option.Id,
              Prices: prices.length ? prices : [{ Price: option.Price || 0 }],
            };
          }),
          MinSelection: modifier.minChoices,
          MaxSelection: modifier.maxChoices,
        };
      }));
      // });
    });

    var list = Array.from(menuItems.values());
    for (var menu of this.menus) {
      let items = list.filter(_ => _.MenuIds.includes(menu.Id));

      menu.Options = items[0].Options.filter(o => items.every(_ => _.Options.some(_o => JSON.stringify(_o) == JSON.stringify(o))));

      items.forEach(_ => {
        _.Options = _.Options.filter(o => !menu.Options.some(_o => JSON.stringify(_o) == JSON.stringify(o)));
      });
    }

    this.menuItems = list;
  }

  private promotions: any[];
  private makePromotions() {
    const promotions: Map<string, any> = new Map();
    var pVariations = this.data.items.filter(_ => _.type == 'deal').map(deal =>
      deal.variations.flatMap(_ => { return { ..._, name: deal.name || _.name, description: deal.description || _.name }; })
    ).flat();

    pVariations.forEach((item: any) => {
      if (!promotions.has(item.id))
        promotions.set(item.id, {
          Id: Guid.newGuid(),
          BusinessId: this.business.Id,
          Name: item.name,
          ServingHours: [],
          VisibleInMenuIds: [],
          Items: [],
          Price: {
            DiscountType: 'FixPrice',
            Value: item.basePrice,
          },
          UI: {
            Description: item.description,
          },
          Order: promotions.size
        });
    });

    this.promotions = Array.from(promotions.values());
  }

  private mapToOperatingHours(data: any[]) {
    let operatingHours: any[] = [];

    data.forEach((serviceType: any) => {
      let hours: { [key: string]: any } = {};

      serviceType.timesPerDay.forEach((times: any) => {

        times.times.forEach((time: any) => {
          const key = `${serviceType.serviceType} -${time.fromLocalTime} -${time.toLocalTime} `;
          hours[key] = hours[key] || {
            OrderType: this.inferOrderType(serviceType.serviceType),
            Days: [],
            StartTime: this.formatTimeSpan(time.fromLocalTime),
            EndTime: this.formatTimeSpan(time.toLocalTime)
          };
          hours[key].Days.push(times.dayOfWeek);

        });

      });

      operatingHours.push(...Object.values(hours));
    });

    return operatingHours;
  }

  private inferOptionTypeFromName(name: string) {
    let value = name.toLowerCase();
    if (value.includes('"') || value.includes('size')) return 'Size';
    if (value.includes('drink')) return 'Drink';
    if (value.includes('topping')) return 'Topping';
    if (value.includes('dip')) return 'Dip';
    if (value.includes('sauce') || value.includes('dressing')) return 'Dressing';
    if (value.includes('crust') || value.includes(' pan')) return 'Crust';
    if (value.includes('instruction')) return 'Instruction';
    return 'Custom';
  }
  private inferMenuTypeFromName(name: string) {
    let value = name.toLowerCase();
    if (value.includes('side') || value.includes('salad')) return 'Side';
    if (value.includes('drink') || value.includes('beer') || value.includes('wine')) return 'Drink';
    if (value.includes('dessert')) return 'Dessert';
    if (value.includes('starter')) return 'Starter';
    if (value.includes('main') || value.includes('pizza') || value.includes('pasta')) return 'Main';
    return 'Other';
  }

  private getModifierGroupPrices(id: string) {
    let modifierGroupPrices: { [id: string]: number } = {};
    for (var group of this.data.modifierGroups)
      if (group.modifiers.includes(id))
        modifierGroupPrices[group.id] = this.data.modifierSets.find(_ => _.id == id)?.modifier?.additionPrice || 0;
    return modifierGroupPrices;
  }

  private formatTimeSpan(value: string) {
    let parts = value.split(':');
    return `${parts[0]?.padStart(2, '0') || '00'}:${parts[1]?.padStart(2, '0') || '00'}:${parts[2]?.padStart(2, '0') || '00'}`;
  }

  private inferOrderType(value: string) {
    switch (value.toLocaleLowerCase()) {
      case 'dinein': return 'DineIn';
      case 'delivery': return 'Delivery';
      case 'Pickup': case 'collection': return 'Pickup';
      default: return 'Default';
    }
  }
}
